import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Landmark, Stamp, Trash2, UserPlus, Upload, CheckCircle2, AlertTriangle, MapPin, Calendar, Users, Lock } from "lucide-react";

const fmt = (s) => (s ? new Date(s).toLocaleString() : "—");
const LOCKED = ["APPROVED_COMPENSATION_PAYABLE", "COMPLETED", "CLOSED"];

export default function NotaryJobDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [ncfg, setNcfg] = useState({ id_methods: [], attestation_text: "" });
  const [newSigner, setNewSigner] = useState({ legal_name: "", address: "", city: "", state: "", zip: "", id_method: "", notarial_act: "" });
  const [evCat, setEvCat] = useState("signature_page");
  const [evSigner, setEvSigner] = useState("");
  const [evBusy, setEvBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/notary/portal/assignments/${id}`); setJob(data); }
    catch (e) { toast.error(extractErrorMessage(e, "Could not load assignment")); nav("/server/notary"); }
  }, [id, nav]);

  useEffect(() => { load(); api.get("/notary/config").then(({ data }) => setNcfg(data)).catch(() => {}); }, [load]);

  useEffect(() => {
    if (user && !["notary", "both"].includes(user.service_capability)) nav("/server", { replace: true });
  }, [user, nav]);

  if (!job) return <p className="mx-auto max-w-3xl text-sm text-slate-400">Loading…</p>;

  const locked = LOCKED.includes(job.status);
  const comp = job.completion_state;
  const signers = job.signers || [];
  const evidence = job.evidence || [];

  const addSigner = async () => {
    if (!newSigner.legal_name.trim()) { toast.error("Signer legal/printed name is required"); return; }
    try { await api.post(`/notary/portal/assignments/${id}/signers`, newSigner);
      setNewSigner({ legal_name: "", address: "", city: "", state: "", zip: "", id_method: "", notarial_act: "" });
      toast.success("Signer added"); await load(); }
    catch (e) { toast.error(extractErrorMessage(e, "Could not add signer")); }
  };
  const delSigner = async (sid) => { try { await api.delete(`/notary/portal/assignments/${id}/signers/${sid}`); await load(); } catch (e) { toast.error(extractErrorMessage(e, "Delete failed")); } };
  const patchSigner = async (sid, patch) => { try { await api.patch(`/notary/portal/assignments/${id}/signers/${sid}`, patch); await load(); } catch (e) { toast.error(extractErrorMessage(e, "Update failed")); } };
  const uploadEvidence = async (e) => {
    const file = (e.target.files || [])[0]; if (!file) return;
    setEvBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/notary/portal/assignments/${id}/evidence`, { file_id: data.id, filename: data.filename, category: evCat, signer_id: evSigner || null });
      toast.success("Evidence uploaded"); await load();
    } catch (err) { toast.error(extractErrorMessage(err, "Upload failed")); }
    finally { setEvBusy(false); }
  };
  const delEvidence = async (eid) => { try { await api.delete(`/notary/portal/assignments/${id}/evidence/${eid}`); await load(); } catch (e) { toast.error(extractErrorMessage(e, "Delete failed")); } };
  const viewEvidence = async (fid) => { try { const { data } = await api.post(`/files/${fid}/view-token`); window.open(`${API}/files/${fid}?token=${data.token}&inline=true`, "_blank"); } catch { toast.error("Could not open file"); } };
  const saveAttestation = async () => { try { await api.post(`/notary/portal/assignments/${id}/attestation`, { confirmed: true }); toast.success("Attestation recorded"); await load(); } catch (e) { toast.error(extractErrorMessage(e, "Failed")); } };
  const toggleAppt = async (val) => { try { await api.post(`/notary/portal/assignments/${id}/appointment-completed`, { appointment_completed: val }); await load(); } catch (e) { toast.error(extractErrorMessage(e, "Failed")); } };
  const submitCompletion = async () => { try { const { data } = await api.post(`/notary/portal/assignments/${id}/submit-completion`); toast.success("Submitted for admin approval"); await load(); return data; } catch (e) { toast.error(extractErrorMessage(e, "Not complete yet")); } };

  return (
    <div className="mx-auto max-w-3xl space-y-4" data-testid="notary-portal-detail">
      <button type="button" data-testid="notary-portal-back" onClick={() => nav("/server/notary")} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> Back to Notary Assignments
      </button>

      {/* Assignment header */}
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${job.enhanced_evidence ? "bg-amber-100 text-amber-700" : "bg-indigo-100 text-indigo-700"}`}>
            {job.enhanced_evidence ? <Landmark className="h-6 w-6" /> : <Stamp className="h-6 w-6" />}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-xl font-bold text-slate-900">{job.client_name}</h2>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600" data-testid="notary-portal-status">{job.status}</span>
              {job.enhanced_evidence && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Enhanced Evidence</span>}
              {locked && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><Lock className="h-3 w-3" /> Locked</span>}
            </div>
            <p className="text-sm text-slate-500">{job.service_type || "Notary"} · {job.document_category || "—"}</p>
            <div className="mt-2 grid grid-cols-1 gap-1 text-sm text-slate-600 sm:grid-cols-2">
              <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-slate-400" /> {job.num_signers} signer(s) required</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4 text-slate-400" /> {fmt(job.appointment_datetime)}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-slate-400" /> {job.service_address || job.county || "—"}</span>
              <span className="text-slate-500">{job.client_phone} · {job.client_email}</span>
            </div>
            {job.special_requirements && <p className="mt-2 rounded bg-slate-50 p-2 text-xs text-slate-600">{job.special_requirements}</p>}
          </div>
        </div>
      </div>

      {/* Notary Service Record */}
      <div className="space-y-3 rounded-xl border border-indigo-200 bg-indigo-50/30 p-4" data-testid="notary-portal-record">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Notary Service Record</p>
          {comp && <span data-testid="notary-portal-completion-count" className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${comp.complete ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{comp.done_count}/{comp.total} complete</span>}
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" data-testid="notary-portal-appt-completed" disabled={locked} checked={!!(job.notary_service_record?.appointment_completed)} onChange={(e) => toggleAppt(e.target.checked)} className="h-4 w-4" />
          Appointment completed
        </label>

        {/* Signers */}
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="notary-portal-signers">
          <p className="mb-1.5 text-xs font-semibold text-slate-600">Signers ({signers.length}/{job.num_signers} required)</p>
          <div className="space-y-2">
            {signers.map((s) => (
              <div key={s.id} className="rounded border border-slate-200 p-2 text-xs" data-testid={`notary-portal-signer-${s.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{s.legal_name}</span>
                  {!locked && <button data-testid={`notary-portal-signer-del-${s.id}`} onClick={() => delSigner(s.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
                <p className="text-slate-500">{[s.address, s.city, s.state, s.zip].filter(Boolean).join(", ") || "No address"}</p>
                <div className="mt-1 grid grid-cols-2 gap-1.5">
                  <select data-testid={`notary-portal-signer-idmethod-${s.id}`} disabled={locked} value={s.id_method || ""} onChange={(e) => patchSigner(s.id, { id_method: e.target.value })} className="rounded border border-slate-300 px-1.5 py-1 text-xs">
                    <option value="">ID method…</option>
                    {(ncfg.id_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <Input data-testid={`notary-portal-signer-act-${s.id}`} disabled={locked} defaultValue={s.notarial_act || ""} placeholder="Notarial act" onBlur={(e) => { if ((e.target.value || "") !== (s.notarial_act || "")) patchSigner(s.id, { notarial_act: e.target.value }); }} className="h-7 text-xs" />
                </div>
              </div>
            ))}
            {signers.length === 0 && <p className="text-xs text-slate-400">No signers yet. Add each signer below.</p>}
          </div>
          {!locked && (
            <div className="mt-2 grid grid-cols-2 gap-1.5 rounded border border-dashed border-indigo-300 p-2">
              <Input data-testid="notary-portal-newsigner-name" className="col-span-2 h-8" placeholder="Legal / printed name *" value={newSigner.legal_name} onChange={(e) => setNewSigner({ ...newSigner, legal_name: e.target.value })} />
              <Input data-testid="notary-portal-newsigner-address" className="col-span-2 h-8" placeholder="Address" value={newSigner.address} onChange={(e) => setNewSigner({ ...newSigner, address: e.target.value })} />
              <Input data-testid="notary-portal-newsigner-city" className="h-8" placeholder="City" value={newSigner.city} onChange={(e) => setNewSigner({ ...newSigner, city: e.target.value })} />
              <div className="grid grid-cols-2 gap-1.5">
                <Input data-testid="notary-portal-newsigner-state" className="h-8" placeholder="State" value={newSigner.state} onChange={(e) => setNewSigner({ ...newSigner, state: e.target.value })} />
                <Input data-testid="notary-portal-newsigner-zip" className="h-8" placeholder="ZIP" value={newSigner.zip} onChange={(e) => setNewSigner({ ...newSigner, zip: e.target.value })} />
              </div>
              <select data-testid="notary-portal-newsigner-idmethod" value={newSigner.id_method} onChange={(e) => setNewSigner({ ...newSigner, id_method: e.target.value })} className="h-8 rounded border border-slate-300 px-1.5 text-xs">
                <option value="">ID method (if applicable)…</option>
                {(ncfg.id_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <Input data-testid="notary-portal-newsigner-act" className="h-8" placeholder="Notarial act" value={newSigner.notarial_act} onChange={(e) => setNewSigner({ ...newSigner, notarial_act: e.target.value })} />
              <Button size="sm" data-testid="notary-portal-add-signer" onClick={addSigner} className="col-span-2 bg-indigo-600 text-white hover:bg-indigo-700"><UserPlus className="mr-1 h-3.5 w-3.5" /> Add signer</Button>
            </div>
          )}
        </div>

        {/* Evidence */}
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="notary-portal-evidence">
          <p className="mb-1.5 text-xs font-semibold text-slate-600">Evidence {job.enhanced_evidence ? <span className="text-amber-700">(signature-page evidence required)</span> : <span className="text-slate-400">(optional)</span>}</p>
          <div className="space-y-1">
            {evidence.map((ev) => (
              <div key={ev.id} className="flex items-center justify-between rounded border border-slate-200 px-2 py-1 text-xs" data-testid={`notary-portal-evidence-${ev.id}`}>
                <span className="truncate text-slate-700"><span className="rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold uppercase text-slate-500">{ev.category}</span> {ev.filename}</span>
                <span className="flex items-center gap-2">
                  <button data-testid={`notary-portal-evidence-view-${ev.id}`} onClick={() => viewEvidence(ev.file_id)} className="text-indigo-600 hover:underline">View</button>
                  {!locked && <button data-testid={`notary-portal-evidence-del-${ev.id}`} onClick={() => delEvidence(ev.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>}
                </span>
              </div>
            ))}
            {evidence.length === 0 && <p className="text-xs text-slate-400">No evidence uploaded.</p>}
          </div>
          {!locked && (
            <>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                <select data-testid="notary-portal-evidence-cat" value={evCat} onChange={(e) => setEvCat(e.target.value)} className="h-8 rounded border border-slate-300 px-1.5 text-xs">
                  <option value="signature_page">Signature page</option>
                  <option value="id">ID document</option>
                  <option value="document">Document</option>
                  <option value="other">Other</option>
                </select>
                <select data-testid="notary-portal-evidence-signer" value={evSigner} onChange={(e) => setEvSigner(e.target.value)} className="h-8 rounded border border-slate-300 px-1.5 text-xs">
                  <option value="">Not signer-specific</option>
                  {signers.map((s) => <option key={s.id} value={s.id}>{s.legal_name}</option>)}
                </select>
              </div>
              <label data-testid="notary-portal-evidence-upload" className="mt-1.5 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-xs text-slate-600 hover:border-indigo-400">
                <Upload className="h-4 w-4" /> {evBusy ? "Uploading…" : "Upload evidence / photo"}<input type="file" className="hidden" disabled={evBusy} onChange={uploadEvidence} />
              </label>
              <p className="mt-1 text-[11px] text-slate-400">ID photos are only required when this assignment's policy requires it. ID policy: <b>{job.id_evidence_policy}</b>.</p>
            </>
          )}
        </div>

        {/* Attestation */}
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="notary-portal-attestation">
          <p className="text-xs font-semibold text-slate-600">Notary attestation</p>
          <p className="mt-1 text-[11px] text-slate-500">{ncfg.attestation_text || "I certify that I completed the assigned notarial services."}</p>
          {job.attestation?.confirmed ? (
            <p className="mt-1.5 text-xs font-medium text-emerald-700" data-testid="notary-portal-attestation-done">✓ Attested by {job.attestation.by} · {fmt(job.attestation.at)}</p>
          ) : (
            !locked && <Button size="sm" data-testid="notary-portal-attest-btn" onClick={saveAttestation} className="mt-1.5 bg-slate-900 text-white hover:bg-slate-800">Confirm attestation</Button>
          )}
        </div>

        {/* Completion */}
        {comp && (
          <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="notary-portal-completion">
            <p className="mb-1.5 text-xs font-semibold text-slate-600">Completion requirements</p>
            <ul className="space-y-1">
              {comp.items.filter((i) => i.required).map((i) => (
                <li key={i.key} data-testid={`notary-portal-check-${i.key}`} className="flex items-center gap-2 text-xs">
                  {i.done ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
                  <span className={i.done ? "text-slate-600" : "text-slate-800"}>{i.label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2">
              {!LOCKED.includes(job.status) && job.status !== "COMPLETED_PENDING_APPROVAL" && (
                <Button size="sm" data-testid="notary-portal-submit-completion" disabled={!comp.complete} onClick={submitCompletion} className="bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">Submit completion for approval</Button>
              )}
              {job.status === "COMPLETED_PENDING_APPROVAL" && <span className="text-xs font-medium text-amber-700" data-testid="notary-portal-pending-approval">Submitted — pending admin approval</span>}
              {job.status === "APPROVED_COMPENSATION_PAYABLE" && <span className="text-xs font-medium text-emerald-700" data-testid="notary-portal-approved">✓ Approved by admin — compensation payable</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
